/**
 * Who an access case waits on, in words (5 Oct, product owner's UAT).
 *
 * "It should be clear which ones are waiting on what": the server says, per
 * case and per item, who has the next move and what it is (`waiting_on`);
 * these put it in the reader's language the same way on the list and on the
 * case page — "Backoffice Team (365FO, Bluestar)", "Waiting on Marcus Montie
 * to approve".
 */

import { useCallback, useMemo } from "react";

import { useT } from "@/i18n";
import type { AccessWaiting } from "@/lib/api";

import { itemLabelKey, itemShortKey } from "../lib/access";

export function useWaitingWords() {
  const { t } = useT("tickets");

  /** An item's short name for a dot ("365FO"); its full name if it has none. */
  const shortName = useCallback(
    (key: string) => {
      const short = t(itemShortKey(key) as never, { defaultValue: "" });
      return short === "" ? t(itemLabelKey(key) as never) : short;
    },
    [t],
  );

  /** Who, or "an admin" when the matrix names nobody. */
  const who = useCallback(
    (wait: Pick<AccessWaiting, "who">) => wait.who ?? t("access.waiting.anyone"),
    [t],
  );

  /** One wait on the list: "Marcus Montie (approval: 365FO Sales for USL)". */
  const caseEntry = useCallback(
    (wait: AccessWaiting) =>
      t(`access.waiting.short.${wait.what}` as never, {
        who: who(wait),
        items: (wait.items ?? []).map(shortName).join(", "),
        details: (wait.details ?? []).join("; "),
      }),
    [t, who, shortName],
  );

  /** One wait on an item row: "Waiting on Backoffice Team to complete". */
  const itemEntry = useCallback(
    (wait: AccessWaiting) => {
      const line = t(`access.waiting.item.${wait.what}` as never, { who: who(wait) });
      const details = (wait.details ?? []).join("; ");
      return details === "" ? line : `${line}: ${details}`;
    },
    [t, who],
  );

  /** A case's waits as one line: "Waiting on: A (account) · B (365FO, Bluestar)". */
  const caseLine = useCallback(
    (waits: readonly AccessWaiting[]) =>
      waits.length === 0
        ? null
        : t("access.waiting.label", { list: waits.map(caseEntry).join(" · ") }),
    [t, caseEntry],
  );

  return useMemo(
    () => ({ shortName, caseEntry, itemEntry, caseLine }),
    [shortName, caseEntry, itemEntry, caseLine],
  );
}
